import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, XCircle } from "lucide-react";

function formatElapsed(seconds) {
  const minutes = Math.floor(seconds / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  return `${minutes}:${rest}`;
}

function Counter({ label, value, tone }) {
  return (
    <div className={`rounded-lg border p-3 ${tone}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}

function RowsTable({ rows }) {
  return (
    <div className="overflow-auto border rounded-lg max-h-96">
      <table className="w-full text-xs">
        <thead className="bg-gray-100 text-left sticky top-0">
          <tr>
            <th className="p-2 w-20">Line</th>
            <th className="p-2 w-44">Registration Number</th>
            <th className="p-2">Reason</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.line}-${index}`} className="border-t align-top">
              <td className="p-2 font-bold">{row.line}</td>
              <td className="p-2">{row.registration_number || "-"}</td>
              <td className="p-2">{row.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Loading({ fileName, isStudents }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="rounded-lg border border-blue-300 bg-blue-50 p-6 text-sm text-blue-900 flex flex-col items-center gap-3 text-center">
      <RefreshCw size={36} className="animate-spin" />
      <div className="text-lg font-bold">Importing {fileName || "file"}...</div>
      <div className="text-2xl font-bold tabular-nums">{formatElapsed(elapsed)}</div>
      {isStudents && (
        <div className="max-w-xl">
          About 100 students are imported per minute, so a file with 4,000
          students can take around 40 minutes. When it finishes, this panel
          lists every row that could not be imported.
        </div>
      )}
      <div className="max-w-xl font-bold">
        Keep this page open until the report appears.
      </div>
    </div>
  );
}

function StudentsReport({ report }) {
  const failed = report.failed || [];
  const skipped = report.skipped || [];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Counter label="Rows in the file" value={report.total} tone="bg-gray-50" />
        <Counter
          label="Imported"
          value={report.created}
          tone="border-green-300 bg-green-50 text-green-800"
        />
        <Counter
          label="Already existed (skipped)"
          value={skipped.length}
          tone="border-gray-300 bg-gray-50"
        />
        <Counter
          label="Failed"
          value={failed.length}
          tone={
            failed.length > 0
              ? "border-red-300 bg-red-50 text-red-800"
              : "border-gray-300 bg-gray-50"
          }
        />
      </div>

      {failed.length === 0 ? (
        <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 p-4 text-sm text-green-800">
          <CheckCircle2 size={18} />
          All students were processed without errors.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertTriangle size={18} className="shrink-0" />
            <span>
              {failed.length} {failed.length === 1 ? "row was" : "rows were"} not
              imported. All the other rows were imported.
            </span>
          </div>
          <RowsTable rows={failed} />
        </div>
      )}

      {skipped.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer font-bold">
            Skipped rows ({skipped.length})
          </summary>
          <div className="pt-2">
            <RowsTable rows={skipped} />
          </div>
        </details>
      )}
    </div>
  );
}

export default function ImportStatus({ loading, fileName, importType, result, error }) {
  if (loading) {
    return <Loading fileName={fileName} isStudents={importType === "Students"} />;
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
        <XCircle size={18} className="shrink-0" />
        <div className="flex flex-col gap-1">
          <div className="font-bold">The import did not finish: {error}</div>
          <div>
            The server may have kept importing in the background. Students
            that were already imported are skipped if you run the same file
            again, so it is safe to import it again.
          </div>
        </div>
      </div>
    );
  }

  if (!result) return null;

  if (importType === "Students" && Array.isArray(result.failed)) {
    return <StudentsReport report={result} />;
  }

  return (
    <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 p-4 text-sm text-green-800">
      <CheckCircle2 size={18} />
      {result.message || "Data imported!"}
    </div>
  );
}
